import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../api/axios";
import { toast } from "react-toastify";
import { usePermissions } from "../../hooks/usePermissions";
import Pagination from "../../components/Pagination";
import ConfirmationModal from "../../components/ConfirmationModal";
import SortableTableHeader from "../../components/SortableTableHeader";
import { CopyHash } from "../../features/web3/forex/components/CopyHash";
import { bscAddressUrl } from "../../lib/format";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import * as XLSX from "xlsx";

const GUSDWithdrawals = () => {
  const [exportFormat, setExportFormat] = useState("csv");
  const { token } = useSelector((state) => state.auth);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 10,
    startDate: "",
    endDate: "",
    status: "",
    username: "",
  });
  const [tempFilters, setTempFilters] = useState({
    page: 1,
    pageSize: 10,
    startDate: "",
    endDate: "",
    status: "",
    username: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRows: 0,
  });
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedWithdrawals, setSelectedWithdrawals] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  const [showRejectConfirmModal, setShowRejectConfirmModal] = useState(false);
  const [showBulkApproveConfirmModal, setShowBulkApproveConfirmModal] =
    useState(false);
  const [showBulkRejectConfirmModal, setShowBulkRejectConfirmModal] =
    useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const [sortedWithdrawals, setSortedWithdrawals] = useState([]);
  const { can } = usePermissions();

  const fetchWithdrawals = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.status) params.status = filters.status;
      if (filters.username) params.username = filters.username;

      const response = await axiosInstance.get(
        "/api/admin/withdrawls/gusd/history",
        { headers: { token }, params },
      );

      if (response.data.success) {
        const newWithdrawals = response.data.data.data;
        setWithdrawals(newWithdrawals);
        setSortedWithdrawals(newWithdrawals);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
      toast.error("Failed to fetch withdrawals");
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    if (!withdrawals.length) return;
    const sorted = [...withdrawals].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key];
      let bValue = b[sort.key];
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";
      if (
        sort.key === "amount" ||
        sort.key === "fee" ||
        sort.key === "payable"
      ) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === "sno") {
        const indexA = withdrawals.findIndex((w) => w._id === a._id);
        const indexB = withdrawals.findIndex((w) => w._id === b._id);
        return sort.direction === "asc" ? indexA - indexB : indexB - indexA;
      }
      if (sort.key === "status") {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === "createdAt" || sort.key === "updatedAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
    setSortedWithdrawals(sorted);
  }, [withdrawals, sort]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === "page" ? value : 1,
    }));
  };

  const handleTempFilterChange = (key, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === "page" ? value : 1,
    }));
  };

  const applyFilters = () => {
    const updatedFilters = { ...tempFilters };
    if (updatedFilters.status === "pending") {
      updatedFilters.pageSize = 500;
    }
    setFilters(updatedFilters);
    if (updatedFilters.status === "pending") {
      setTempFilters(updatedFilters);
    }
  };

  const handlePageChange = (newPage) => {
    handleFilterChange("page", newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    handleFilterChange("pageSize", newPageSize);
    handleFilterChange("page", 1);
  };

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleApprove = async (withdrawId) => {
    const withdrawal = withdrawals.find((w) => w._id === withdrawId);
    setSelectedWithdrawal(withdrawal);
    setShowApproveConfirmModal(true);
  };

  const confirmApprove = async () => {
    if (!selectedWithdrawal) {
      toast.error("No withdrawal selected");
      return;
    }
    try {
      setLoadingAction(true);
      const response = await axiosInstance.post(
        "/api/admin/withdrawls/approve",
        { withdrawId: selectedWithdrawal._id },
        { headers: { token } },
      );
      if (response.data.success) {
        toast.success("Withdrawal approved successfully");
        setShowApproveConfirmModal(false);
        fetchWithdrawals();
      }
    } catch (error) {
      console.error("Error approving withdrawal:", error);
      toast.error("Failed to approve withdrawal");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleReject = async (withdrawId) => {
    const withdrawal = withdrawals.find((w) => w._id === withdrawId);
    setSelectedWithdrawal(withdrawal);
    setShowRejectConfirmModal(true);
  };

  const confirmReject = async () => {
    if (!selectedWithdrawal) {
      toast.error("No withdrawal selected");
      return;
    }
    try {
      setLoadingAction(true);
      const response = await axiosInstance.post(
        "/api/admin/withdrawls/reject",
        { withdrawId: selectedWithdrawal._id },
        { headers: { token } },
      );
      if (response.data.success) {
        toast.success("Withdrawal rejected successfully");
        setShowRejectConfirmModal(false);
        fetchWithdrawals();
      }
    } catch (error) {
      console.error("Error rejecting withdrawal:", error);
      toast.error("Failed to reject withdrawal");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleBulkApprove = async () => {
    setShowBulkApproveConfirmModal(true);
  };

  const confirmBulkApprove = async () => {
    try {
      setBulkLoading(true);
      const response = await axiosInstance.post(
        "/api/admin/withdrawls/bulk-approve",
        { withdrawIds: selectedWithdrawals },
        { headers: { token } },
      );
      if (response.data.success) {
        const {
          newApprovedIds,
          alreadyApprovedIds,
          alreadyRejectedIds,
          alreadyCancelledIds,
        } = response.data;
        let message = `Bulk approval completed. `;
        if (newApprovedIds.length > 0)
          message += `${newApprovedIds.length} newly approved. `;
        if (alreadyApprovedIds.length > 0)
          message += `${alreadyApprovedIds.length} already approved. `;
        if (alreadyRejectedIds.length > 0)
          message += `${alreadyRejectedIds.length} already rejected. `;
        if (alreadyCancelledIds.length > 0)
          message += `${alreadyCancelledIds.length} already cancelled.`;
        toast.success(message);
        setSelectedWithdrawals([]);
        setShowBulkApproveConfirmModal(false);
        fetchWithdrawals();
      }
    } catch (error) {
      console.error("Error bulk approving withdrawals:", error);
      toast.error("Failed to bulk approve withdrawals");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkReject = async () => {
    setShowBulkRejectConfirmModal(true);
  };

  const confirmBulkReject = async () => {
    try {
      setBulkLoading(true);
      const response = await axiosInstance.post(
        "/api/admin/withdrawls/bulk-reject",
        { withdrawIds: selectedWithdrawals },
        { headers: { token } },
      );
      if (response.data.success) {
        const {
          newRejectedIds,
          alreadyApprovedIds,
          alreadyRejectedIds,
          alreadyCancelledIds,
        } = response.data;
        let message = `Bulk rejection completed. `;
        if (newRejectedIds.length > 0)
          message += `${newRejectedIds.length} newly rejected. `;
        if (alreadyApprovedIds.length > 0)
          message += `${alreadyApprovedIds.length} already approved. `;
        if (alreadyRejectedIds.length > 0)
          message += `${alreadyRejectedIds.length} already rejected. `;
        if (alreadyCancelledIds.length > 0)
          message += `${alreadyCancelledIds.length} already cancelled.`;
        toast.success(message);
        setSelectedWithdrawals([]);
        setShowBulkRejectConfirmModal(false);
        fetchWithdrawals();
      }
    } catch (error) {
      console.error("Error bulk rejecting withdrawals:", error);
      toast.error("Failed to bulk reject withdrawals");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkApproveAllPending = () => {
    const pendingWithdrawals = withdrawals.filter((w) => w.status === 0);
    if (pendingWithdrawals.length === 0) {
      toast.error("No pending withdrawals to approve");
      return;
    }
    const withdrawIds = pendingWithdrawals.map((w) => w._id);
    setSelectedWithdrawals(withdrawIds);
    handleBulkApprove();
  };

  const handleBulkRejectAllPending = () => {
    const pendingWithdrawals = withdrawals.filter((w) => w.status === 0);
    if (pendingWithdrawals.length === 0) {
      toast.error("No pending withdrawals to reject");
      return;
    }
    const withdrawIds = pendingWithdrawals.map((w) => w._id);
    setSelectedWithdrawals(withdrawIds);
    handleBulkReject();
  };

  const handleSelectWithdrawal = (withdrawalId) => {
    setSelectedWithdrawals((prev) =>
      prev.includes(withdrawalId)
        ? prev.filter((id) => id !== withdrawalId)
        : [...prev, withdrawalId],
    );
  };

  const handleSelectAllPending = () => {
    const pendingWithdrawals = withdrawals.filter((w) => w.status === 0);
    const pendingIds = pendingWithdrawals.map((w) => w._id);
    setSelectedWithdrawals(pendingIds);
  };

  const handleClearSelection = () => {
    setSelectedWithdrawals([]);
  };

  const handleExportAll = () => {
    const currentTableData = withdrawals;
    if (!currentTableData || currentTableData.length === 0) {
      toast.error("No withdrawals to export");
      return;
    }
    const selectedStatus = tempFilters.status || "all";
    if (selectedStatus !== "pending") {
      toast.error(
        "Please use 'Export All Details' button for non-pending status",
      );
      return;
    }
    const fileName = `withdrawals_${selectedStatus}_${new Date().toISOString().split("T")[0]}`;
    if (exportFormat === "csv") {
      const csvContent = currentTableData
        .map((w) => `${w.address},${w.payable}`)
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        `Exported ${currentTableData.length} ${selectedStatus} withdrawals to CSV`,
      );
    } else {
      const data = currentTableData.map((w) => ({
        Address: w.address,
        Payable: w.payable,
      }));
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Withdrawals");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      toast.success(
        `Exported ${currentTableData.length} ${selectedStatus} withdrawals to Excel`,
      );
    }
  };

  const handleExportAllDetails = () => {
    const currentTableData = withdrawals;
    if (!currentTableData || currentTableData.length === 0) {
      toast.error("No withdrawals to export");
      return;
    }
    const statusMap = {
      0: "pending",
      1: "approved",
      2: "cancelled",
      3: "rejected",
    };
    const selectedStatus = tempFilters.status || "all";
    const data = currentTableData.map((w) => ({
      Username: w.username,
      WalletAddress: w.walletAddress,
      Amount: w.amount,
      TransactionID: w.tranx_id,
      WalletType: w.wallet_type,
      Fee: w.fee,
      Payable: w.payable,
      Method: w.method,
      Address: w.address,
      Status: statusMap[w.status] || "unknown",
      CreatedAt: w.createdAt,
      UpdatedAt: w.updatedAt,
    }));
    const fileName = `withdrawals_all_details_${selectedStatus}_${new Date().toISOString().split("T")[0]}`;
    if (exportFormat === "csv") {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const csvData = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        `Exported ${data.length} withdrawals with all details to CSV`,
      );
    } else {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Withdrawals");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      toast.success(
        `Exported ${data.length} withdrawals with all details to Excel`,
      );
    }
  };

  const handleExportPendingTxt = () => {
    const pendingWithdrawals = withdrawals.filter((w) => w.status === 0);
    if (pendingWithdrawals.length === 0) {
      toast.error("No pending withdrawals to export");
      return;
    }
    const txtContent = pendingWithdrawals
      .map((w) => `${w.address},${w.payable}`)
      .join("\n");
    const fileName = `pending_withdrawals_${new Date().toISOString().split("T")[0]}.txt`;
    const blob = new Blob([txtContent], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(
      `Exported ${pendingWithdrawals.length} pending withdrawals to TXT`,
    );
  };

  const clearFilters = () => {
    const defaultFilters = {
      page: 1,
      pageSize: 10,
      startDate: "",
      endDate: "",
      status: "",
      username: "",
    };
    setFilters(defaultFilters);
    setTempFilters(defaultFilters);
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
        return (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
            Pending
          </span>
        );
      case 1:
        return (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            Approved
          </span>
        );
      case 2:
        return (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
            Rejected
          </span>
        );
      case 3:
        return (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-white/[0.05] text-muted-foreground border border-border/40">
            Canceled
          </span>
        );
      default:
        return (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-white/[0.05] text-muted-foreground border border-border/40">
            Unknown
          </span>
        );
    }
  };

  const ActionDropdown = ({ withdrawal }) => (
    <div className="inline-block text-left">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label="Withdrawal actions"
            type="button"
            className="group inline-flex items-center gap-1 rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-foreground hover:bg-white/[0.06] transition-colors"
          >
            Actions
            <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-44">
          {withdrawal.status === 0 && (
            <>
              {can("POST:/api/admin/withdrawls/approve") && (
                <DropdownMenuItem
                  onSelect={() => handleApprove(withdrawal._id)}
                  disabled={loadingAction}
                  className="px-4 py-2 text-xs text-emerald-400 focus:bg-emerald-500/10 focus:text-emerald-400"
                >
                  Approve
                </DropdownMenuItem>
              )}
              {can("POST:/api/admin/withdrawls/reject") && (
                <DropdownMenuItem
                  onSelect={() => handleReject(withdrawal._id)}
                  disabled={loadingAction}
                  className="px-4 py-2 text-xs text-red-400 focus:bg-red-500/10 focus:text-red-400"
                >
                  Reject
                </DropdownMenuItem>
              )}
            </>
          )}
          {withdrawal.status !== 0 && (
            <div className="px-4 py-2 text-xs text-muted-foreground">
              No actions available
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const inputCls =
    "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
  const labelCls =
    "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Finance
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        GUSD <span className="text-gradient-ginox">Withdrawals</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        View and manage GUSD withdrawal requests
      </p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className={labelCls}>Username / Trx ID / WalletAddress / Payout Address</label>
            <input
              type="text"
              value={tempFilters.username}
              onChange={(e) =>
                handleTempFilterChange("username", e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
              }}
              placeholder="Search…"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Start Date</label>
            <input
              type="date"
              value={tempFilters.startDate}
              onChange={(e) =>
                handleTempFilterChange("startDate", e.target.value)
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input
              type="date"
              value={tempFilters.endDate}
              onChange={(e) =>
                handleTempFilterChange("endDate", e.target.value)
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select
              value={tempFilters.status}
              onChange={(e) => handleTempFilterChange("status", e.target.value)}
              className={inputCls}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select
              value={tempFilters.pageSize}
              onChange={(e) =>
                handleTempFilterChange("pageSize", parseInt(e.target.value))
              }
              className={inputCls}
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page</label>
            <select
              value={tempFilters.page}
              onChange={(e) =>
                handleTempFilterChange("page", parseInt(e.target.value))
              }
              className={inputCls}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex gap-2 flex-wrap items-center">
          <button
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Clear Filters
          </button>
          <button
            onClick={handleExportAll}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Export
          </button>
          <button
            onClick={handleExportAllDetails}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Export All Details
          </button>
          <button
            onClick={handleExportPendingTxt}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Export Pending (TXT)
          </button>
          <div className="flex items-center gap-2">
            <label className={labelCls + " mb-0"}>Format</label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="rounded-md border border-border/70 bg-white/[0.03] px-2 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="csv">CSV</option>
              <option value="excel">Excel</option>
            </select>
          </div>
          {can("POST:/api/admin/withdrawls/bulk-approve") && (
            <button
              onClick={handleBulkApproveAllPending}
              disabled={bulkLoading}
              className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
            >
              {bulkLoading ? "Processing..." : "Bulk Approve All Pending"}
            </button>
          )}
          {can("POST:/api/admin/withdrawls/bulk-reject") && (
            <button
              onClick={handleBulkRejectAllPending}
              disabled={bulkLoading}
              className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-xs text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
            >
              {bulkLoading ? "Processing..." : "Bulk Reject All Pending"}
            </button>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <table className="w-full border-separate border-spacing-0">
            <tbody>
              <tr>
                <td
                  colSpan={13}
                  className="py-12 text-center text-sm text-muted-foreground"
                >
                  Loading...
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <SortableTableHeader
                    sortKey="sno"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    S.No
                  </SortableTableHeader>
                  <SortableTableHeader className="text-left" sortable={false}>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={
                          withdrawals.length > 0 &&
                          withdrawals.filter((w) => w.status === 0).length ===
                            selectedWithdrawals.length
                        }
                        onChange={handleSelectAllPending}
                        className="h-3.5 w-3.5 rounded border-border/70 bg-white/[0.03] accent-ginox-green"
                      />
                      <span>Select</span>
                    </div>
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="username"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Username
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="name"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Name
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="surname"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Surname
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="amount"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Amount
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="tranx_id"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Transaction ID
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="wallet_type"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Wallet Type
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="fee"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Fee
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="payable"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Payable
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="method"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Method
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="address"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Address
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="status"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Status
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="createdAt"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Created At
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="updatedAt"
                    currentSort={sort}
                    onSort={handleSort}
                    className="text-left"
                  >
                    Updated At
                  </SortableTableHeader>
                  <SortableTableHeader className="text-left" sortable={false}>
                    Actions
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedWithdrawals.map((withdrawal, index) => (
                  <tr
                    key={withdrawal._id}
                    className="group hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono">
                      {(filters.page - 1) * filters.pageSize + index + 1}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      {withdrawal.status === 0 && (
                        <input
                          type="checkbox"
                          checked={selectedWithdrawals.includes(withdrawal._id)}
                          onChange={() =>
                            handleSelectWithdrawal(withdrawal._id)
                          }
                          className="h-3.5 w-3.5 rounded border-border/70 bg-white/[0.03] accent-ginox-green"
                        />
                      )}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      <div>{withdrawal.username}</div>
                      <div className="text-xs text-muted-foreground/80">
                        <CopyHash
                          value={withdrawal.walletAddress}
                          href={bscAddressUrl(withdrawal.walletAddress)}
                          head={6}
                          tail={4}
                        />
                      </div>
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      {withdrawal.name || "—"}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      {withdrawal.surname || "—"}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      {withdrawal.amount}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      {withdrawal.tranx_id}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      {withdrawal.wallet_type}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      {withdrawal.fee}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      {withdrawal.payable}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      {withdrawal.method}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      <CopyHash
                        value={withdrawal.address}
                        href={bscAddressUrl(withdrawal.address)}
                        head={6}
                        tail={4}
                      />
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5">
                      {getStatusBadge(withdrawal.status)}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono whitespace-nowrap">
                      {formatDate(withdrawal.createdAt)}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono whitespace-nowrap">
                      {formatDate(withdrawal.updatedAt)}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      <ActionDropdown withdrawal={withdrawal} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Bulk Actions for Selected Withdrawals */}
            {selectedWithdrawals.length > 0 && (
              <div className="border-t border-border/40 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {selectedWithdrawals.length} withdrawal(s) selected
                  </span>
                  <button
                    onClick={handleClearSelection}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
                <div className="flex gap-2">
                  {can("POST:/api/admin/withdrawls/bulk-approve") && (
                    <button
                      onClick={() => handleBulkApprove()}
                      disabled={bulkLoading}
                      className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
                    >
                      {bulkLoading
                        ? "Processing..."
                        : `Approve Selected (${selectedWithdrawals.length})`}
                    </button>
                  )}
                  {can("POST:/api/admin/withdrawls/bulk-reject") && (
                    <button
                      onClick={() => handleBulkReject()}
                      disabled={bulkLoading}
                      className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                    >
                      {bulkLoading
                        ? "Processing..."
                        : `Reject Selected (${selectedWithdrawals.length})`}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <ConfirmationModal
          isOpen={showApproveConfirmModal}
          onClose={() => setShowApproveConfirmModal(false)}
          onConfirm={confirmApprove}
          title="Approve Withdrawal"
          message={`Are you sure you want to approve the withdrawal of ${selectedWithdrawal?.amount} GUSD for user ${selectedWithdrawal?.username}?`}
          confirmText="Approve"
          loading={loadingAction}
          loadingText="Approving..."
        />
        <ConfirmationModal
          isOpen={showRejectConfirmModal}
          onClose={() => setShowRejectConfirmModal(false)}
          onConfirm={confirmReject}
          title="Reject Withdrawal"
          message={`Are you sure you want to reject the withdrawal of ${selectedWithdrawal?.amount} GUSD for user ${selectedWithdrawal?.username}?`}
          confirmText="Reject"
          loading={loadingAction}
          loadingText="Rejecting..."
          danger={true}
        />
        <ConfirmationModal
          isOpen={showBulkApproveConfirmModal}
          onClose={() => setShowBulkApproveConfirmModal(false)}
          onConfirm={confirmBulkApprove}
          title="Bulk Approve Withdrawals"
          message={`Are you sure you want to approve ${selectedWithdrawals.length} selected withdrawal(s)?`}
          confirmText="Approve All"
          loading={bulkLoading}
          loadingText="Approving..."
        />
        <ConfirmationModal
          isOpen={showBulkRejectConfirmModal}
          onClose={() => setShowBulkRejectConfirmModal(false)}
          onConfirm={confirmBulkReject}
          title="Bulk Reject Withdrawals"
          message={`Are you sure you want to reject ${selectedWithdrawals.length} selected withdrawal(s)?`}
          confirmText="Reject All"
          loading={bulkLoading}
          loadingText="Rejecting..."
          danger={true}
        />
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default GUSDWithdrawals;
